import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "@/utils/adminFetch";

export default function AdminGalleryManager() {
  const [photos, setPhotos] = useState([]);
  const [newTitleIt, setNewTitleIt] = useState("");
  const [newTitleEn, setNewTitleEn] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery-photos`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPhotos)
      .catch(() => {});
  };
  useEffect(load, []);

  const saveTitle = async (p, lang, value) => {
    try {
      await adminFetch(`/admin/gallery-photos/${p.photo_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(lang === "it" ? { title_it: value } : { title_en: value }),
      });
      toast.success("Titolo aggiornato");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.title.it}"?`)) return;
    try {
      await adminFetch(`/admin/gallery-photos/${p.photo_id}`, { method: "DELETE" });
      setPhotos((ps) => ps.filter((x) => x.photo_id !== p.photo_id));
      toast.success("Foto eliminata");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const add = async () => {
    if (!newFile || !newTitleIt.trim()) {
      toast.error("Servono una foto e un titolo italiano");
      return;
    }
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("file", newFile);
      const up = await adminFetch("/admin/upload", { method: "POST", body: fd });
      const doc = await adminFetch("/admin/gallery-photos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ src: up.url, title_it: newTitleIt.trim(), title_en: newTitleEn.trim() }),
      });
      setPhotos((ps) => [...ps, doc]);
      setNewFile(null);
      setNewTitleIt("");
      setNewTitleEn("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Foto aggiunta alla gallery");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="mt-10" data-testid="admin-gallery">
      {/* ADD FORM */}
      <div className="border border-stone2/60 p-5 mb-8" data-testid="admin-gallery-add-form">
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50 mb-4">Aggiungi una foto alla Gallery</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">File immagine</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => setNewFile(e.target.files?.[0] || null)}
              data-testid="admin-gallery-file"
              className="mt-1.5 block text-sm text-ink/60 file:mr-3 file:border file:border-stone2 file:bg-ivory file:px-3 file:py-2 file:text-xs file:uppercase file:tracking-[0.1em] hover:file:border-medblue"
            />
          </div>
          <input
            placeholder="Titolo (IT) *"
            value={newTitleIt}
            onChange={(e) => setNewTitleIt(e.target.value)}
            data-testid="admin-gallery-title-it"
            className="border border-stone2 bg-ivory px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-medblue"
          />
          <input
            placeholder="Titolo (EN)"
            value={newTitleEn}
            onChange={(e) => setNewTitleEn(e.target.value)}
            data-testid="admin-gallery-title-en"
            className="border border-stone2 bg-ivory px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-medblue"
          />
          <button
            type="button"
            onClick={add}
            disabled={adding}
            data-testid="admin-gallery-add-button"
            className="inline-flex items-center gap-2 bg-medblue text-ivory text-[10px] font-medium uppercase tracking-[0.15em] px-5 py-3 hover:bg-ink disabled:opacity-40 transition-colors duration-300"
          >
            {adding ? <Upload size={13} className="animate-pulse" /> : <Plus size={13} />} Aggiungi
          </button>
        </div>
      </div>

      {/* GRID */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((p) => (
          <div key={p.photo_id} className="border border-stone2/60 bg-ivory" data-testid={`admin-photo-${p.photo_id}`}>
            <div className="aspect-[4/3] overflow-hidden bg-stone">
              <img src={p.src} alt={p.title.it} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-3 space-y-2">
              <input
                defaultValue={p.title.it}
                onBlur={(e) => e.target.value !== p.title.it && saveTitle(p, "it", e.target.value)}
                data-testid={`admin-photo-title-it-${p.photo_id}`}
                className="w-full border border-stone2/60 bg-ivory px-2.5 py-1.5 text-xs text-ink focus:outline-none focus:border-medblue"
              />
              <input
                defaultValue={p.title.en}
                onBlur={(e) => e.target.value !== p.title.en && saveTitle(p, "en", e.target.value)}
                data-testid={`admin-photo-title-en-${p.photo_id}`}
                className="w-full border border-stone2/60 bg-ivory px-2.5 py-1.5 text-xs text-ink/60 focus:outline-none focus:border-medblue"
              />
              <button
                type="button"
                onClick={() => remove(p)}
                data-testid={`admin-photo-delete-${p.photo_id}`}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-red-600/70 hover:text-red-600 transition-colors"
              >
                <Trash2 size={11} /> Elimina
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
